// ChatBox08/index.js - Conversa Livre baseado no ChatBox04 (VERSÃO COMPLETA)
import React, { useContext, useState, useEffect, useRef } from 'react';
import { CoreContext } from "context/CoreContext";
import {
 ChatContainer,
 ChatHeader,
 BackButton,
 ChatAvatar,
 ChatTitle,
 ChatMessages,
 ChatInputContainer,
 ChatTextarea,
 ChatButton,
 MessageContainer,
 Message,
 MainContainer,
 CarouselContainer,
 ContentWrapper,
 AudioButton,
 AudioButtonContainer,
 ProgressContainer,
 ProgressBar
} from './styled';

import ContainerAuthenticated from "containers/Authenticated";
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';

// Limite de mensagens gratuitas (20/dia por personagem)
import FreeLimitModal from 'components/FreeLimitModal';
import MessageCounter from 'components/MessageCounter';
import TypingIndicator from 'components/TypingIndicator';
import AIDisclaimer from 'components/AIDisclaimer';
import ChatMessageText from 'components/ChatMessageText';
import useFreeMessageLimit from 'hooks/useFreeMessageLimit';
import useConversationHistory from 'hooks/useConversationHistory';
import { SendChatMessage } from 'services/chat';
import { TranscreverAudio } from 'services/transcricao';
import { useHistory } from 'react-router-dom';

const CHARACTER_ID = 'davi';
const CHARACTER_NAME = 'Davi';
const CONVERSATION_MODE = 'chat'; // Bate-papo

const ChatBox08 = ({ videoTitle }) => {
 const [messages, setMessages] = useState([]);
 const [userMessage, setUserMessage] = useState('');
 const chatMessagesRef = useRef(null);
 const [conversationId, setConversationId] = useState('');

 // Estados para gravação de áudio
 const [isRecording, setIsRecording] = useState(false);
 const [mediaRecorder, setMediaRecorder] = useState(null);
 const [loading, setLoading] = useState(false);
 const [progress, setProgress] = useState(0);
 // Indicador "digitando..." enquanto o personagem processa a resposta
 const [isTyping, setIsTyping] = useState(false);
 
 // Estados para controle de UI do iOS
 const [showIOSHelp, setShowIOSHelp] = useState(false);
 const [microphoneWorking, setMicrophoneWorking] = useState(null);

 const { currentProfile, user } = useContext(CoreContext);

 // Hook do limite de mensagens
 const history = useHistory();
 const limit = useFreeMessageLimit(CHARACTER_ID);
 // Hook do histórico de conversas (localStorage) — separado por personagem + modo
 const conversationHistory = useConversationHistory(CHARACTER_ID, CONVERSATION_MODE);

 useEffect(() => {
   if (user && user.id) {
     const newConversationId = `${user.id}_Chat08_Conversation`;
     setConversationId(newConversationId);
     console.log('Conversation ID definido como:', newConversationId);
   }
 }, [user]);

 // Carrega histórico do localStorage quando o hook estiver pronto
 useEffect(() => {
   if (conversationHistory.isReady && conversationHistory.loadedMessages.length > 0) {
     setMessages(conversationHistory.loadedMessages);
   }
 }, [conversationHistory.isReady]);

 // Verificar estado do microfone no iOS ao carregar
 useEffect(() => {
   if (isIOS()) {
     const microphoneTestedKey = `microphone_tested_${user?.id || 'guest'}`;
     const wasTested = localStorage.getItem(microphoneTestedKey);
     
     if (wasTested === 'true') {
       setMicrophoneWorking(true);
       setShowIOSHelp(false);
     } else {
       setShowIOSHelp(true);
       setMicrophoneWorking(null);
     }
   }
 }, [user]);

 const appendMessage = (sender, message) => {
   const newMessage = {
     sender,
     message: formatMessage(message),
     timestamp: new Date().toISOString()
   };

   setMessages((prevMessages) => [...prevMessages, newMessage]);

   // Salva mensagem no histórico de conversas (localStorage)
   conversationHistory.saveMessage(newMessage);
 };

 useEffect(() => {
   if (chatMessagesRef.current) {
     chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
   }
 }, [messages]);

 const sendMessage = async () => {
   if (!userMessage.trim()) return;

   // Verifica limite de mensagens (apenas usuários gratuitos)
   const allowed = await limit.checkBeforeSend();
   if (!allowed) return;

   appendMessage(currentProfile?.name, userMessage);
   const sentMessage = userMessage;
   setUserMessage('');

   // Registra que o usuário gastou uma mensagem
   await limit.registerSent();

   // Ativa indicador "digitando..."
   setIsTyping(true);

   try {
     // Passa pelo NOSSO servidor: exige login e sabe para onde encaminhar
     const resultado = await SendChatMessage({
       personagem: CHARACTER_ID,
       modo: CONVERSATION_MODE,
       query: sentMessage,
       conversationId,
     });
     appendMessage('Rei Davi', resultado.answer);
     if (resultado.conversation_id) {
       setConversationId(resultado.conversation_id);
     }
   } catch (error) {
     console.error('Falha ao buscar mensagem:', error);
     appendMessage('Rei Davi', 'Algo aconteceu, mas estou aqui para conversar! Vamos continuar?');
   } finally {
     setIsTyping(false);
   }
 };

 // Função para detectar iOS/Safari
 const isIOS = () => {
   return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
          (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
 };

 const isSafari = () => {
   return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
 };

 // Função para obter o mimeType correto para cada plataforma
 const getAudioMimeType = () => {
   if (isIOS() || isSafari()) {
     if (MediaRecorder.isTypeSupported('audio/mp4')) {
       return 'audio/mp4';
     } else if (MediaRecorder.isTypeSupported('audio/wav')) {
       return 'audio/wav';
     } else {
       return '';
     }
   } else {
     if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
       return 'audio/webm;codecs=opus';
     } else if (MediaRecorder.isTypeSupported('audio/webm')) {
       return 'audio/webm';
     } else {
       return '';
     }
   }
 };

 // Funções para gravação de áudio
 const startRecording = async () => {
   try {
     if (isIOS() && window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
       alert('Para usar o microfone no iPhone, é necessário acessar via HTTPS.');
       return;
     }

     if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
       alert('Seu navegador não suporta gravação de áudio.');
       return;
     }

     console.log('🎤 Solicitando acesso ao microfone...');
     const stream = await navigator.mediaDevices.getUserMedia({ 
       audio: {
         echoCancellation: true,
         noiseSuppression: true,
         autoGainControl: true
       }
     });
     
     const mimeType = getAudioMimeType();
     console.log('🎤 MimeType selecionado:', mimeType);
     
     const recorderOptions = mimeType ? { mimeType } : {};
     const recorder = new MediaRecorder(stream, recorderOptions);
     
     const chunks = [];
     
     recorder.ondataavailable = (event) => {
       if (event.data.size > 0) {
         chunks.push(event.data);
       }
     };
     
     recorder.onstop = () => {
       const finalMimeType = mimeType || 'audio/wav';
       const blob = new Blob(chunks, { type: finalMimeType });
       console.log('🎤 Áudio gravado:', blob.type, blob.size, 'bytes');
       handleRecordingComplete(blob);
     };
     
     recorder.start();
     setMediaRecorder(recorder);
     setIsRecording(true);
     console.log('🎤 Gravação iniciada com sucesso');
     
     if (isIOS() && microphoneWorking !== true) {
       const microphoneTestedKey = `microphone_tested_${user?.id || 'guest'}`;
       localStorage.setItem(microphoneTestedKey, 'true');
       setMicrophoneWorking(true);
       setShowIOSHelp(false);
     }
   } catch (error) {
     console.error('❌ Erro ao iniciar gravação:', error);
     
     if (isIOS()) {
       setMicrophoneWorking(false);
       setShowIOSHelp(true);
     }
     
     if (error.name === 'NotAllowedError') {
       if (isIOS()) {
         alert('Permissão negada para o microfone.\n\nPara habilitar:\n1. Vá em Ajustes > Safari > Câmera e Microfone\n2. Permita acesso ao microfone\n3. Recarregue a página\n\nUse o botão Azul com a Lupa para testar depois de configurar.');
       } else {
         alert('Permissão negada para o microfone. Clique no ícone de cadeado na barra de endereços e permita o acesso ao microfone.');
       }
     } else if (error.name === 'NotFoundError') {
       alert('Nenhum microfone foi encontrado no dispositivo.');
     } else if (error.name === 'NotSupportedError') {
       alert('Gravação de áudio não é suportada neste navegador.');
     } else if (error.name === 'AbortError') {
       alert('Acesso ao microfone foi interrompido.');
     } else {
       alert(`Erro ao acessar o microfone: ${error.message}`);
     }
   }
 };

 const stopRecording = () => {
   if (mediaRecorder && isRecording) {
     mediaRecorder.stop();
     mediaRecorder.stream.getTracks().forEach(track => track.stop());
     setIsRecording(false);
     console.log('🎤 Gravação parada');
   }
 };

 const testMicrophonePermissions = async () => {
   try {
     console.log('🧪 Testando permissões do microfone...');
     const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
     console.log('✅ Permissões OK');
     stream.getTracks().forEach(track => track.stop());
     
     const microphoneTestedKey = `microphone_tested_${user?.id || 'guest'}`;
     localStorage.setItem(microphoneTestedKey, 'true');
     
     setMicrophoneWorking(true);
     setShowIOSHelp(false);
     
     setTimeout(() => {
       alert('✅ Microfone configurado com sucesso!\n\nAgora você pode usar o botão verde 🎤 para gravar áudio.');
     }, 100);
   } catch (error) {
     console.error('❌ Erro no teste:', error);
     setMicrophoneWorking(false);
     setShowIOSHelp(true);
     
     if (error.name === 'NotAllowedError') {
       alert('❌ Permissão negada!\n\nPara habilitar:\n1. Vá em Ajustes > Safari > Câmera e Microfone\n2. Permita acesso ao microfone\n3. Recarregue a página e teste novamente');
     } else {
       alert(`❌ Problema no microfone: ${error.name} - ${error.message}`);
     }
   }
 };

 const handleRecordingComplete = async (blob) => {
   console.log('=== INÍCIO DO PROCESSO ===');
   console.log('1. Áudio gravado com sucesso:', blob);
   console.log('Tamanho do áudio:', blob.size, 'bytes');
   console.log('Tipo do áudio:', blob.type);
   console.log('Plataforma:', isIOS() ? 'iOS' : 'Outras');

   if (blob.size === 0) {
     alert('Erro: Áudio vazio. Tente gravar novamente.');
     return;
   }

   // Verifica limite de mensagens (apenas usuários gratuitos)
   const allowed = await limit.checkBeforeSend();
   if (!allowed) return;

   setLoading(true);
   setProgress(0);


   const updateProgress = () => {
     setProgress((prev) => (prev < 90 ? prev + 10 : prev));
   };

   const interval = setInterval(updateProgress, 500);

   try {
     console.log('2. Iniciando transcrição com Groq...');
     console.time('Audio to Text API');
     // O audio vai pelo NOSSO servidor: a chave do Groq nao fica mais no navegador
     const transcribedText = await TranscreverAudio(blob);
     console.timeEnd('Audio to Text API');
     
     console.log('3. Transcrição bem-sucedida:', transcribedText);
     
     setUserMessage(transcribedText);
     appendMessage(currentProfile?.name, transcribedText);
     setUserMessage('');

     // Registra que o usuário gastou uma mensagem (envio por áudio)
     await limit.registerSent();

     // Ativa indicador "digitando..."
     setIsTyping(true);

     try {
       // Passa pelo NOSSO servidor: exige login e sabe para onde encaminhar
       const resultado = await SendChatMessage({
         personagem: CHARACTER_ID,
         modo: CONVERSATION_MODE,
         query: transcribedText,
         conversationId,
       });
       appendMessage('Rei Davi', resultado.answer);
       if (resultado.conversation_id) {
         setConversationId(resultado.conversation_id);
       }
     } catch (error) {
       console.error('Falha ao buscar mensagem:', error);
       appendMessage('Rei Davi', 'Algo aconteceu, mas estou aqui para conversar! Vamos continuar?');
     }
     
   } catch (error) {
     console.error('ERRO NA TRANSCRIÇÃO:');
     console.error('Status:', error.response?.status);
     console.error('Dados:', error.response?.data);
     console.error('Mensagem:', error.message);
     alert('Erro na transcrição do áudio. Tente novamente.');
   } finally {
     setLoading(false);
     setProgress(0);
     setIsTyping(false);
     clearInterval(interval);
   }
 };

 const formatMessage = (message) => {
  // Processar links no formato markdown [texto](url)
  const linkRegex = /\[([^\]]+)]\((https?:\/\/[^\s)]+)\)/g;
  const wordRegex = /https?:\/\/(?:www\.)?([\w-]+)(?=\/|\?|$)/;

  let formatted = message
    .replace(linkRegex, (match, text, url) => {
      const wordMatch = url.match(wordRegex);
      return wordMatch ? `<a href="${url}" target="_blank">${wordMatch[1]}</a>` : `<a href="${url}" target="_blank">${text}</a>`;
    })
    .replace(wordRegex, (match, word) => `<a href="${match}" target="_blank">${word}</a>`);

  // Pensamento do personagem (0): marcadores POR FORA do parêntese -> **(texto)** ou *(texto)*
  formatted = formatted.replace(/[\*_]{1,2}\s*\(([^)\n]+)\)\s*[\*_]{1,2}/g, '<span class="thought">($1)</span>');
  // Pensamento do personagem (1): com marcadores (*texto*) ou (_texto_) -> itálico suave unificado
  formatted = formatted.replace(/\(\s*[\*_]{1,2}([^)]+?)[\*_]{1,2}\s*\)/g, '<span class="thought">($1)</span>');
  // Pensamento do personagem (2 - plano B): parêntese sozinho na própria linha (com ou sem marcadores)
  formatted = formatted.replace(/^[ \t]*\(([^)\n]+)\)[ \t]*$/gm, (m, inner) => `<span class="thought">(${inner.replace(/[\*_]/g, '')})</span>`);

  // Processar negrito: **texto** ou *texto*
  formatted = formatted.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  formatted = formatted.replace(/\*([^*]+)\*/g, '<strong>$1</strong>');
  
  // Processar itálico: __texto__ ou _texto_ (mas evitar _ no meio de palavras)
  formatted = formatted.replace(/__([^_]+)__/g, '<em>$1</em>');
  formatted = formatted.replace(/\s_([^_]+)_\s/g, ' <em>$1</em> ');
  
  // Processar listas com marcadores (- item ou * item)
  formatted = formatted.replace(/^[\-\*]\s+(.+)$/gm, '<li>$1</li>');
  
  // Envolver listas em <ul> se houver <li>
  if (formatted.includes('<li>')) {
    formatted = formatted.replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>');
  }
  
  // Processar quebras de linha
  formatted = formatted.replace(/\n/g, '<br>');

  return formatted;
};

 const responsive = {
   superLargeDesktop: {
     breakpoint: { max: 4000, min: 3000 },
     items: 1
   },
   desktop: {
     breakpoint: { max: 3000, min: 1024 },
     items: 1
   },
   tablet: {
     breakpoint: { max: 1024, min: 464 },
     items: 1
   },
   mobile: {
     breakpoint: { max: 464, min: 0 },
     items: 1
   }
 };

 return (
   <ContainerAuthenticated primary page={'ChatBox08'}>
     <MainContainer noBanner={limit.isPremium}>
       <ContentWrapper noBanner={limit.isPremium}>
         {/* Banner de anúncios — exibido apenas para usuários gratuitos */}
         {!limit.isPremium && (
         <CarouselContainer>
           <Carousel
             responsive={responsive}
             infinite={true}
             autoPlay={true}
             autoPlaySpeed={3000}
             showDots={false}
             removeArrowOnDeviceType={["tablet", "mobile"]}
             dotListClass="custom-dot-list-style"
           >
             <div>
               <img
                 src="/images/banner_1.png"
                 alt="Anúncio 1"
                 style={{ width: '100%', height: 'auto' }}
               />
             </div>
             <div>
               <img
                 src="/images/banner_2.png"
                 alt="Anúncio 2"
                 style={{ width: '100%', height: 'auto' }}
               />
             </div>
             <div>
               <img
                 src="/images/banner_3.png"
                 alt="Anúncio 2"
                 style={{ width: '100%', height: 'auto' }}
               />
             </div>
             <div>
               <img
                 src="/images/banner_4.png"
                 alt="Anúncio 2"
                 style={{ width: '100%', height: 'auto' }}
               />
             </div>
             <div>
               <img
                 src="/images/banner_5.png"
                 alt="Anúncio 3"
                 style={{ width: '100%', height: 'auto' }}
               />
             </div>
           </Carousel>
         </CarouselContainer>
         )}

         <ChatContainer noBanner={limit.isPremium}>
           <ChatHeader>
             <BackButton onClick={() => history.push('/dashboard/conversation')} aria-label="Voltar para escolha de personagem">
               ←
             </BackButton>
             <ChatAvatar image="/images/davi_pixel.png" />
             <ChatTitle>Rei Davi · Bate-papo</ChatTitle>
           </ChatHeader>
           <MessageCounter
             characterId={CHARACTER_ID}
             isPremium={limit.isPremium}
             onUpgradeClick={() => history.push('/dashboard/sign')}
           />
           <AIDisclaimer />
           <ChatMessages ref={chatMessagesRef}>
             <MessageContainer>
               {messages.map((msg, index) => (
                 <Message key={index} isUser={msg.sender === currentProfile?.name}>
                   <ChatMessageText sender={msg.sender} html={msg.message} isUser={msg.sender === currentProfile?.name} />
                 </Message>
               ))}
               {isTyping && <TypingIndicator characterName={CHARACTER_NAME} />}
             </MessageContainer>
           </ChatMessages>
           <ChatInputContainer>
             <ChatTextarea
               value={userMessage}
               onChange={(e) => setUserMessage(e.target.value)}
               onKeyDown={(e) => {
                 if (e.key === 'Enter' && !e.shiftKey) {
                   e.preventDefault();
                   sendMessage();
                 }
               }}
               rows="2"
               placeholder="Chat aberto para qualquer conversa..."
             />
             <AudioButtonContainer>
               {isIOS() && (microphoneWorking === null || microphoneWorking === false) && (
                 <AudioButton
                   onClick={testMicrophonePermissions}
                   style={{ backgroundColor: '#2196F3' }}
                   title="Testar microfone no iPhone"
                 >
                   <img 
                     src="/icons/test.svg" 
                     alt="Testar" 
                     style={{ width: '16px', height: '16px', filter: 'brightness(0) invert(1)' }}
                   />
                 </AudioButton>
               )}
               
               <AudioButton
                 onClick={isRecording ? stopRecording : startRecording}
                 isRecording={isRecording}
                 disabled={loading}
                 title={isIOS() ? 'Gravar áudio (iOS)' : 'Gravar áudio'}
               >
                 {loading ? (
                   <img 
                     src="/icons/loading.svg" 
                     alt="Processando" 
                     style={{ 
                       width: '18px', 
                       height: '18px', 
                       filter: 'brightness(0) invert(1)',
                       animation: 'spin 1s linear infinite'
                     }}
                   />
                 ) : isRecording ? (
                   <img 
                     src="/icons/stop.svg" 
                     alt="Parar" 
                     style={{ width: '18px', height: '18px', filter: 'brightness(0) invert(1)' }}
                   />
                 ) : (
                   <img 
                     src="/icons/microphone.svg" 
                     alt="Gravar" 
                     style={{ width: '18px', height: '18px', filter: 'brightness(0) invert(1)' }}
                   />
                 )}
               </AudioButton>
               <ChatButton onClick={sendMessage} title="Enviar mensagem">
                 <img 
                   src="/icons/send.svg" 
                   alt="Enviar" 
                   style={{ width: '18px', height: '18px', filter: 'brightness(0) invert(1)' }}
                 />
               </ChatButton>
             </AudioButtonContainer>
           </ChatInputContainer>
           {loading && (
             <ProgressContainer>
               <ProgressBar progress={progress} />
               <span>Transcrevendo áudio... {progress}%</span>
             </ProgressContainer>
           )}
           
           {isIOS() && showIOSHelp && (
             <div style={{
               position: 'absolute',
               bottom: '70px',
               left: '10px',
               right: '10px',
               background: 'rgba(33, 150, 243, 0.9)',
               color: 'white',
               padding: '8px 12px',
               borderRadius: '4px',
               fontSize: '11px',
               lineHeight: '1.3',
               display: 'flex',
               justifyContent: 'space-between',
               alignItems: 'flex-start'
             }}>
               <span>
                 📱 <strong>iPhone:</strong> Para usar o microfone clique no botão Azul com a Lupa.
               </span>
               <button
                 onClick={() => setShowIOSHelp(false)}
                 style={{
                   background: 'none',
                   border: 'none',
                   color: 'white',
                   cursor: 'pointer',
                   fontSize: '14px',
                   padding: '0',
                   marginLeft: '8px'
                 }}
                 title="Fechar aviso"
               >
                 ✕
               </button>
             </div>
           )}
         </ChatContainer>
         <FreeLimitModal
           show={limit.showLimitModal}
           onClose={() => limit.setShowLimitModal(false)}
           characterName={CHARACTER_NAME}
           remaining={limit.remaining}
         />
       </ContentWrapper>
     </MainContainer>
   </ContainerAuthenticated>
 );
};

export default ChatBox08; 